/**
 * Game over (REDESIGN.md 7.9): the Golden Amphora, the winner's name, one
 * line, the final standings, and Play again / Home / scorecard.
 */
import { useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { GameState } from '../../../engine';
import { useAssets } from '../../assets';
import { fmt } from '../../brand';
import type { SeatInfo } from '../../game/types';
import { listNames, standings, who } from '../../game/viewModel';
import { reducedMotion } from '../../lib/motion';
import { Pic, PlayerMedallion, Tibbs } from '../Art';
import { AmphoraIcon, IconHome, IconScores } from '../Icons';

/** Laurel leaves, jade and gold, for three seconds. */
function Laurels() {
  const bits = useMemo(
    () =>
      Array.from({ length: 36 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        dur: 1.8 + Math.random() * 1.2,
        size: 10 + Math.random() * 8,
        rot: Math.random() * 360,
        gold: i % 3 === 0,
      })),
    [],
  );
  return (
    <div className="laurels" aria-hidden="true">
      {bits.map((b, i) => (
        <span
          key={i}
          className={`leaf${b.gold ? ' gold' : ''}`}
          style={{ left: `${b.left}%`, animationDelay: `${b.delay}s`, animationDuration: `${b.dur}s`, width: b.size, height: b.size * 0.5, '--r': `${b.rot}deg` } as CSSProperties}
        />
      ))}
    </div>
  );
}

export function GameOver({
  state,
  seats,
  me,
  mode,
  isHost,
  onPlayAgain,
  onLobby,
  onHome,
  onScores,
}: {
  state: GameState;
  seats: SeatInfo[];
  me: number | null;
  mode: 'solo' | 'online';
  isHost: boolean;
  onPlayAgain: () => void;
  onLobby?: () => void;
  onHome: () => void;
  onScores: () => void;
}) {
  const a = useAssets();
  const winners = state.winners ?? [];
  const rows = standings(state, winners);
  const iWon = me !== null && winners.includes(me);
  const names = winners.map((i) => seats[i]?.name || state.players[i]?.name || (i === me ? 'You' : ''));
  const headline = listNames(names);
  const botWon = !iWon && winners.length > 0 && winners.every((i) => seats[i]?.isBot);
  const others = winners.filter((i) => i !== me).map((i) => seats[i]?.name ?? '');
  const line = iWon ? 'Well played.' : botWon ? 'Next round’s on you.' : `Well played, ${listNames(others)}.`;

  useEffect(() => {
    document.title = `Ancient Nine · ${iWon ? 'You won' : `${headline} won`}`;
    return () => {
      document.title = 'Ancient Nine';
    };
  }, [iWon, headline]);

  return (
    <div className="gameover" role="dialog" aria-modal="true" aria-labelledby="go-title">
      {iWon && !reducedMotion() && <Laurels />}
      <div className="gameover-card panel">
        <div className="go-trophy">
          <Pic
            img={iWon ? a.trophyWin : (a.trophyOther ?? a.trophyWin)}
            eager
            fallback={
              <span className="go-trophy-fallback">
                <AmphoraIcon size={84} />
                <Tibbs pose={iWon ? 'celebrate' : 'applaud'} size={72} />
              </span>
            }
          />
        </div>
        <h2 id="go-title" className="go-title num">
          {headline}
        </h2>
        <p className="go-line">{line}</p>
        <ol className="go-table">
          {rows.map((r) => (
            <li key={r.seat} className={`go-row${r.seat === me ? ' me' : ''}${winners.includes(r.seat) ? ' win' : ''}`}>
              <span className="go-rank num">{r.place}</span>
              <PlayerMedallion seat={r.seat} size={26} />
              <span className="go-name">{who(seats, me, r.seat)}</span>
              {winners.includes(r.seat) && (
                <span className="go-amph" aria-label="winner">
                  <Pic img={a.amphora} fallback={<AmphoraIcon size={22} />} />
                </span>
              )}
              <span className="go-total num">{fmt(r.total)}</span>
            </li>
          ))}
        </ol>
        <div className="go-actions">
          {mode === 'solo' || isHost ? (
            <button className="btn btn-gold btn-big" onClick={onPlayAgain} data-autofocus>
              Play again
            </button>
          ) : (
            <span className="ready-wait">
              <span className="spinner small" aria-hidden="true" /> Waiting for the host…
            </span>
          )}
          <div className="go-secondary">
            {mode === 'online' && isHost && onLobby && (
              <button className="btn btn-ink btn-sm" onClick={onLobby}>
                Lobby
              </button>
            )}
            <button className="btn btn-ink btn-sm" onClick={onHome}>
              <IconHome /> Home
            </button>
            <button className="icon-btn go-sc" onClick={onScores} aria-label="Final scorecard">
              <IconScores />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
